/**
 * NapkinSketchesPanel — bulk "Generate All Napkin Sketches" + stitch to MP4/PNG.
 *
 * Lives on the Shot Breakdown stage. Wires the ticket's 5-state button:
 *   1. Generate All Napkin Sketches     (default when ≥1 shot missing)
 *   2. Generating… X/Y                  (live progress, thumbs populate)
 *   3. Retry N Failed                   (only if failures at end)
 *   4. Stitch Napkin Sketches           (all shots have napkin or slate)
 *   5. Re-stitch Napkin Sketches        (after any pencil regen)
 *
 * Never touches Stage 8 `sketch_url` or the sketches panel. Reads from
 * `quick_sketch_url` per shot via GET /api/projects/{id}/napkins.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles,
  Loader2,
  RotateCcw,
  Film,
  Grid3X3,
  Download,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Pencil,
} from 'lucide-react';
import NapkinWorkbench, { AuthorModelBadge } from './NapkinWorkbench';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 3000;

function fileURL(url) {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
}

export function NapkinSketchesPanel({ projectId, shotCount, onNapkinsChanged }) {
  const [data, setData] = useState(null);
  const [firing, setFiring] = useState(false);
  const [stitching, setStitching] = useState(false);
  const [gridOpen, setGridOpen] = useState(true);
  const [wbShot, setWbShot] = useState(null); // {shot_id, label} → opens workbench
  const pollTimerRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/napkins`);
      setData(res.data);
      return res.data;
    } catch (e) {
      console.error('[Napkins] load failed', e);
      return null;
    }
  }, [projectId]);

  // Live polling while bulk generation OR stitch is running.
  useEffect(() => {
    refresh();
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [refresh]);

  useEffect(() => {
    const isRunning = data?.processing || data?.stitch?.processing;
    if (isRunning) {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = setInterval(refresh, POLL_MS);
    } else if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
      // Notify parent so the per-shot pencil thumbnails refresh from server.
      if (onNapkinsChanged) onNapkinsChanged();
    }
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [data?.processing, data?.stitch?.processing, refresh, onNapkinsChanged]);

  const state = useMemo(() => {
    if (!data) return 'loading';
    if (data.processing) return 'generating';
    if (data.stitch?.processing) return 'stitching';
    // Only the shots that actually failed and are still missing a napkin
    // qualify for retry (a slate is written server-side only during stitch).
    if (data.failed > 0 && data.done + data.failed >= data.total) return 'retry';
    // Every shot has either a napkin OR is done — ready to stitch/re-stitch.
    if (data.total > 0 && data.pending === 0 && data.in_flight === 0) {
      if (data.stitch?.has_output && data.stitch?.stale) return 'restitch';
      if (data.stitch?.has_output) return 'restitch_optional';
      return 'stitch';
    }
    return 'generate';
  }, [data]);

  const fireBulk = useCallback(async (opts = {}) => {
    setFiring(true);
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/napkins/generate-all`,
        { only_failed: !!opts.only_failed, force: !!opts.force },
      );
      toast.success(res.data.message || 'Napkin generation started.');
      await refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.hint || e.message);
      toast.error(`Bulk napkin failed: ${msg}`);
    } finally {
      setFiring(false);
    }
  }, [projectId, refresh]);

  const fireStitch = useCallback(async () => {
    setStitching(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/napkins/stitch`, {});
      toast.success(res.data.message || 'Stitching napkins…');
      await refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.hint || e.message);
      toast.error(`Stitch failed: ${msg}`);
    } finally {
      setStitching(false);
    }
  }, [projectId, refresh]);

  if (!shotCount) return null;

  const barPct =
    data && data.bulk?.total > 0
      ? Math.min(100, Math.round(((data.bulk.completed + data.bulk.failed) / data.bulk.total) * 100))
      : 0;

  const failedShots = (data?.shots || []).filter((s) => s.status === 'failed');

  // ── Button UI states ──────────────────────────────────────────────
  let Btn;
  if (state === 'loading') {
    Btn = (
      <button
        disabled
        className="px-4 py-2 rounded-xl text-sm font-medium bg-purple-500/10 text-purple-300/60 border border-purple-500/30"
        data-testid="napkin-btn-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
      </button>
    );
  } else if (state === 'generating') {
    Btn = (
      <button
        disabled
        className="px-4 py-2 rounded-xl text-sm font-medium bg-purple-500/15 text-purple-200 border border-purple-500/40 flex items-center gap-2"
        data-testid="napkin-btn-generating"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Generating… {data.bulk?.completed + data.bulk?.failed}/{data.bulk?.total}
      </button>
    );
  } else if (state === 'retry') {
    Btn = (
      <button
        onClick={() => fireBulk({ only_failed: true })}
        disabled={firing}
        className="px-4 py-2 rounded-xl text-sm font-medium bg-amber-500/15 text-amber-200 border border-amber-500/40 hover:bg-amber-500/25 flex items-center gap-2 disabled:opacity-50"
        data-testid="napkin-btn-retry"
      >
        {firing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
        Retry {data.failed} Failed
      </button>
    );
  } else if (state === 'stitch') {
    Btn = (
      <button
        onClick={fireStitch}
        disabled={stitching}
        className="px-4 py-2 rounded-xl text-sm font-medium bg-purple-500/20 text-purple-200 border border-purple-500/50 hover:bg-purple-500/30 flex items-center gap-2 disabled:opacity-50"
        data-testid="napkin-btn-stitch"
      >
        {stitching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
        Stitch Napkin Sketches
      </button>
    );
  } else if (state === 'stitching') {
    Btn = (
      <button
        disabled
        className="px-4 py-2 rounded-xl text-sm font-medium bg-purple-500/15 text-purple-200 border border-purple-500/40 flex items-center gap-2"
        data-testid="napkin-btn-stitching"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Stitching…
      </button>
    );
  } else if (state === 'restitch' || state === 'restitch_optional') {
    Btn = (
      <button
        onClick={fireStitch}
        disabled={stitching}
        className={
          state === 'restitch'
            ? 'px-4 py-2 rounded-xl text-sm font-medium bg-amber-500/20 text-amber-200 border border-amber-500/50 hover:bg-amber-500/30 flex items-center gap-2 disabled:opacity-50'
            : 'px-4 py-2 rounded-xl text-sm font-medium bg-purple-500/10 text-purple-200 border border-purple-500/30 hover:bg-purple-500/20 flex items-center gap-2 disabled:opacity-50'
        }
        data-testid="napkin-btn-restitch"
      >
        {stitching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
        Re-stitch Napkin Sketches
      </button>
    );
  } else {
    // 'generate' — default. On happy paths this button auto-chains into
    // stitch when all shots complete (server-side), so the user sees
    // Generating → Stitching → done as a single flow.
    Btn = (
      <button
        onClick={() => fireBulk()}
        disabled={firing}
        className="px-4 py-2 rounded-xl text-sm font-medium bg-purple-500/20 text-purple-200 border border-purple-500/50 hover:bg-purple-500/30 flex items-center gap-2 disabled:opacity-50"
        data-testid="napkin-btn-generate-all"
      >
        {firing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        Generate & Stitch Napkin Sketches
      </button>
    );
  }

  return (
    <div
      className="mb-4 p-4 rounded-2xl border border-purple-500/25 bg-gradient-to-br from-purple-500/[0.06] to-purple-500/[0.02]"
      data-testid="napkin-sketches-panel"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs uppercase tracking-wider text-purple-300/80 font-semibold">
              Napkin Sketches
            </span>
            {data && (
              <span
                className="text-[11px] text-white/50"
                data-testid="napkin-counts"
              >
                {data.done}/{data.total} · {data.failed > 0 ? `${data.failed} failed · ` : ''}
                {data.pending} pending
              </span>
            )}
            {state === 'restitch' && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 uppercase tracking-wider font-semibold">
                stale
              </span>
            )}
          </div>
          <div className="text-xs text-white/55">
            One click generates the rough-pencil frame on every shot, then auto-stitches into
            an animatic MP4 + contact sheet. Composition gut-check — no cast/asset context.
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2 self-start sm:self-auto">{Btn}</div>
      </div>

      {/* Napkin thumbnail grid — click a thumb to open the Iterate-to-Approve Workbench */}
      {(() => {
        const napkinShots = (data?.shots || []).filter((s) => s.napkin_url);
        if (!napkinShots.length) return null;
        return (
          <div className="mt-3" data-testid="napkin-grid-section">
            <button
              onClick={() => setGridOpen((v) => !v)}
              className="flex items-center gap-1.5 text-[11px] font-semibold text-purple-200/90 hover:text-purple-100 mb-2"
              data-testid="napkin-grid-toggle"
            >
              {gridOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              Napkins ({napkinShots.length}) · click to iterate & approve
            </button>
            {gridOpen && (
              <div
                className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2"
                data-testid="napkin-grid"
              >
                {napkinShots.map((s) => (
                  <button
                    key={s.shot_id}
                    onClick={() => setWbShot({ shot_id: s.shot_id, label: s.canonical_id })}
                    className="group relative rounded-lg overflow-hidden border-2 border-white/10 hover:border-violet-400/70 transition-all text-left"
                    data-testid={`napkin-thumb-${s.canonical_id}`}
                    title={`Open workbench — ${s.canonical_id}`}
                  >
                    <div className="aspect-video bg-black/40">
                      <img src={fileURL(s.napkin_url)} alt={s.canonical_id} className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <span className="flex items-center gap-1 text-[10px] font-semibold text-white bg-violet-500/80 px-2 py-1 rounded">
                        <Pencil className="w-3 h-3" /> Iterate
                      </span>
                    </div>
                    <div className="absolute top-1 left-1 flex items-center gap-1">
                      <AuthorModelBadge model={s.author_model} kind={s.napkin_approved_round_id ? 'opus-revised' : 'base'} />
                    </div>
                    <div className="flex items-center justify-between px-1.5 py-1 bg-black/60">
                      <span className="text-[10px] font-mono text-white/75 truncate">{s.canonical_id}</span>
                      {s.napkin_approved_round_id && (
                        <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" data-testid={`napkin-approved-${s.canonical_id}`} />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Progress bar while generating */}
      {state === 'generating' && data?.bulk?.total > 0 && (
        <div className="mt-3">
          <div className="flex justify-between text-[11px] text-white/40 mb-1">
            <span>{data.bulk.failed > 0 ? `${data.bulk.failed} failed so far` : 'Streaming into shot thumbnails as they land'}</span>
            <span>{barPct}%</span>
          </div>
          <div className="w-full h-1.5 bg-white/5 rounded overflow-hidden">
            <div
              className="h-full bg-purple-400/70 transition-all duration-500"
              style={{ width: `${barPct}%` }}
              data-testid="napkin-progress-bar"
            />
          </div>
        </div>
      )}

      {/* Failure list */}
      {state === 'retry' && failedShots.length > 0 && (
        <div
          className="mt-3 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/25"
          data-testid="napkin-failure-list"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-xs font-semibold text-amber-200">
              {failedShots.length} napkin{failedShots.length === 1 ? '' : 's'} failed
            </span>
          </div>
          <div className="text-[11px] text-amber-100/70 leading-relaxed">
            {failedShots.slice(0, 8).map((s) => s.canonical_id).join(', ')}
            {failedShots.length > 8 && ` … +${failedShots.length - 8} more`}
          </div>
        </div>
      )}

      {/* Stitch outputs — inline video preview + downloads.
          Layout stacks vertically on mobile (default) and goes
          side-by-side from md up so it fits both the CD's laptop
          review flow and on-set phone review. */}
      {data?.stitch?.has_output && (
        <div
          className="mt-3 p-3 rounded-lg bg-white/[0.03] border border-white/10"
          data-testid="napkin-stitch-outputs"
        >
          <div className="flex flex-col md:flex-row gap-3 md:items-start">
            {/* Video preview — full width on mobile, fixed width on desktop */}
            {data.stitch.mp4_url && (
              <div className="w-full md:w-64 shrink-0">
                <video
                  key={data.stitch.mp4_url}
                  src={fileURL(data.stitch.mp4_url)}
                  controls
                  playsInline
                  muted
                  preload="metadata"
                  className="w-full aspect-video rounded-md bg-black border border-white/10"
                  data-testid="napkin-stitch-mp4-preview"
                >
                  Your browser doesn&apos;t support inline video.
                </video>
              </div>
            )}

            {/* Meta + downloads — takes remaining width on desktop, full on mobile */}
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <div className="flex items-center gap-1.5 text-[11px] text-white/60 flex-wrap">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span>
                  v{data.stitch.version} · {data.stitch.meta?.shot_count || 0} shots
                  {data.stitch.meta?.slate_count ? ` (${data.stitch.meta.slate_count} slate)` : ''}
                  {data.stitch.meta?.duration_s ? ` · ${data.stitch.meta.duration_s}s` : ''}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {data.stitch.mp4_url && (
                  <a
                    href={fileURL(data.stitch.mp4_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="text-xs px-3 py-1.5 rounded-lg bg-purple-500/15 text-purple-200 border border-purple-500/30 hover:bg-purple-500/25 flex items-center gap-1.5"
                    data-testid="napkin-stitch-mp4-download"
                  >
                    <Film className="w-3.5 h-3.5" /> Animatic MP4
                    <Download className="w-3 h-3 opacity-70" />
                  </a>
                )}
                {data.stitch.png_url && (
                  <a
                    href={fileURL(data.stitch.png_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs px-3 py-1.5 rounded-lg bg-purple-500/15 text-purple-200 border border-purple-500/30 hover:bg-purple-500/25 flex items-center gap-1.5"
                    data-testid="napkin-stitch-png-download"
                  >
                    <Grid3X3 className="w-3.5 h-3.5" /> Contact Sheet
                    <Download className="w-3 h-3 opacity-70" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stitch error surfacing */}
      {data?.stitch?.error && (
        <div
          className="mt-2 p-2 rounded-lg bg-red-500/10 border border-red-500/25 text-[11px] text-red-200"
          data-testid="napkin-stitch-error"
        >
          <AlertTriangle className="w-3 h-3 inline mr-1" />
          Stitch failed: {data.stitch.error}
        </div>
      )}

      {/* Iterate-to-Approve Workbench modal */}
      <NapkinWorkbench
        open={!!wbShot}
        onClose={() => setWbShot(null)}
        projectId={projectId}
        shotId={wbShot?.shot_id}
        shotLabel={wbShot?.label}
        onApproved={() => { refresh(); if (onNapkinsChanged) onNapkinsChanged(); }}
      />
    </div>
  );
}
